/*
 * Copyright (c) 2025. Haodong JU
 */

import { hillo } from 'hillo'
import { cloudV2Url } from '@/model/dataLayer/api/SaaS/SaaSUtils'
import GlobalConfig from '@/model/global/GlobalSettings'

/**
 * UserBLDetailDTO is a data transfer object that represents detailed information about a user
 * in a specific business layer, including membership status, rewards progress, asset records, etc.
 *
 * @typedef {Object} UserBLDetailDTO
 * @property {string} userId - The user's unique identifier
 * @property {Object} cloudUserInfo - Basic user information including name, email, phone, etc.
 * @property {Object|null} currentMembership - The user's current membership information in the business layer, may be null
 * @property {Array} awardProgress - List of the user's award progress in the business layer
 * @property {Array} assetRecords - List of the user's asset records in the business layer (e.g., coupons)
 * @property {Array} pointTransactions - List of the user's point transactions, defaults to empty array
 * @property {Object|null} membershipSystem - The business layer's membership system information, may be null
 * @property {Array} membershipLevels - List of membership levels in the business layer's membership system, defaults to empty array
 * @property {Object|null} businessLayer - Basic information about the business layer, may be null
 * @property {Array} membershipBenefits - List of membership benefits the user can enjoy, defaults to empty array
 * @property {Array} merchantPromotions - List of current promotions from the merchant, defaults to empty array
 * @property {number} orderCount - Number of orders the user has placed with the business layer, defaults to 0
 */

/**
 * Get detailed information about a user for a specific business layer
 *
 * This endpoint returns comprehensive information about a user in the context of a specific business layer,
 * including their membership status, rewards progress, asset records, and more.
 *
 * @param {string} userId - The user ID
 * @param {number} blId - The business layer ID (defaults to current business layer)
 * @returns {Promise<UserBLDetailDTO>} - Promise that resolves to the user details
 */
export async function getUserBusinessLayerDetail (userId, blId = GlobalConfig.blId) {
  return await hillo.get(cloudV2Url + 'membership/user/users/' + userId + '/business-layers/' + blId + '/details')
}

/**
 * Get orders for a user with pagination
 *
 * @param {string} userId - The user ID
 * @param {number} page - The page number (default: 0)
 * @param {number} size - The page size (default: 20)
 * @returns {Promise<Object>} - Promise that resolves to an OrderResponseDTO containing orders and pagination info
 */
export async function getUserOrders (userId, page = 0, size = 20) {
  return await hillo.get(cloudV2Url + 'cloudOrders/user-orders/' + userId, { page, size })
}
